import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription, timer } from 'rxjs';
import {
  NgbModal,
  NgbModalModule,
  NgbModalRef
} from '@ng-bootstrap/ng-bootstrap';
import {
  ArchiveDataSummary,
  ARCHIVE_CONFIRMATION
} from 'src/app/models/archive-data';
import { DataService, WorkflowContextService } from 'src/app/services';

@Component({
  selector: 'wm-settings-archive-data',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbModalModule],
  templateUrl: './settings-archive-data.component.html',
  styleUrls: ['./settings-archive-data.component.scss']
})
export class SettingsArchiveDataComponent implements OnInit, OnDestroy {
  summary: ArchiveDataSummary | null = null;
  loading = false;
  error = '';
  confirmation = '';
  submitting = false;
  readonly confirmationSentence = ARCHIVE_CONFIRMATION;
  private subscription = new Subscription();
  private request: Subscription | null = null;
  private clientId: string | null = null;
  private confirmationModal: NgbModalRef | null = null;

  constructor(
    public context: WorkflowContextService,
    private modal: NgbModal,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.context.client$.subscribe(client => this.changeClient(client?.id))
    );
    this.changeClient(this.context.client?.id);
    this.subscription.add(
      timer(5000, 5000).subscribe(() => {
        if (this.activeRun && !this.loading && !this.submitting) this.refresh();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.request?.unsubscribe();
    this.confirmationModal?.dismiss();
  }

  get activeRun() {
    return this.summary?.runs.find(
      run => run.status === 'Queued' || run.status === 'Running'
    );
  }

  get canArchive(): boolean {
    return (
      !!this.summary &&
      this.summary.mode === 0 &&
      this.summary.eligibleApplications > 0 &&
      !this.activeRun &&
      !this.loading &&
      !this.submitting
    );
  }

  openConfirmation(content: unknown): void {
    if (!this.canArchive) return;
    this.confirmation = '';
    this.confirmationModal = this.modal.open(content, {
      centered: true,
      backdrop: 'static',
      ariaLabelledBy: 'archive-confirm-title'
    });
  }

  private changeClient(clientId: string | undefined): void {
    if (!clientId || clientId === this.clientId) return;
    this.request?.unsubscribe();
    this.confirmationModal?.dismiss();
    this.clientId = clientId;
    this.summary = null;
    this.error = '';
    this.confirmation = '';
    this.submitting = false;
    this.refresh();
  }

  refresh(): void {
    if (!this.clientId || this.submitting) return;
    this.request?.unsubscribe();
    this.loading = true;
    this.request = this.dataService
      .getArchiveDataSummary(this.clientId)
      .subscribe({
        next: summary => {
          this.summary = summary;
          this.loading = false;
          this.error = '';
        },
        error: () => {
          this.loading = false;
          this.error =
            'Archive details could not be loaded. Refresh to try again.';
        }
      });
  }

  confirmArchive(): void {
    if (
      !this.canArchive ||
      this.confirmation !== this.confirmationSentence ||
      !this.clientId
    )
      return;
    this.submitting = true;
    this.request = this.dataService
      .queueArchiveData(this.clientId, this.confirmation)
      .subscribe({
        next: summary => {
          this.summary = summary;
          this.submitting = false;
          this.confirmation = '';
          this.confirmationModal?.close();
        },
        error: () => {
          this.submitting = false;
          this.confirmationModal?.dismiss();
          this.error =
            'The archive request could not be accepted. Refresh the status before trying again.';
        }
      });
  }
}
